import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import { BrowseFiltersOption } from '../../../components/browseFilterAndSearch/BrowseFiltersOptions/BrowseFiltersOption';
import { FilterDropdown } from '../../../components/browseFilterAndSearch/FiltersDropdown/FiltersDropdown';
import { SearchByButtons } from '../../../components/browseFilterAndSearch/SearchByButtons/SearchByButtons';
import { SearchByKeywords } from '../../../components/browseFilterAndSearch/SearchByKeywords/SearchByKeywords';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import LocationSelect from '../../../components/shared/Location/LocationSelect';
import LocationFilterDropdown from '../../../components/shared/LocationFilterDropdown/LocationFilterDropdown';
import { DEFAULT_PAGE } from '../../../constants/paginationDefaults';
import { useJobFilterContext } from '../../../contexts/JobFilterProvider';
import { IsMobile } from '../../../helpers/IsMobile';
import { getFilterDropdownValues } from '../../../redux/actions/filtersActions';
import { getSkills } from '../../../redux/actions/skillsActions';
import { selectFilterDropdownValues } from '../../../selectors/filterSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';

import styles from './OpeningSearchAndFilter.module.scss';

const OpeningSearchAndFilter = ({ setfilters, filters, setFetchJobsByName }) => {
  const { t } = useTranslation('browse');
  const dispatch = useDispatch();
  const isMobile = IsMobile();
  const inputRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [includeRemote, setIncludeRemote] = useState(true);
  const [locationValue, setLocationValue] = useState(filters.location);
  const [keywordValue, setKeywordValue] = useState(searchParams.get('query'));
  const [valueSelected, setValueSelected] = useState(false);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(filters.location?.aroundRadius || 250);
  const [isUsHeadquarter, setIsUsHeadquarter] = useState(false);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const { organizationLocations } = useSelector(selectFilterDropdownValues);
  const { updateSearchParams } = useJobFilterContext();

  useEffect(() => {
    if (currentOrganization?.id) {
      setIsUsHeadquarter(currentOrganization?.based_in_us_headquarter);
      dispatch(getSkills(currentOrganization?.id));
      dispatch(getFilterDropdownValues(currentOrganization?.id));
    } else if (currentOrganization?.name === 'Boon') {
      dispatch(getFilterDropdownValues('Boon'));
      dispatch(getSkills());
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (!searchParams.size) {
      setKeywordValue('');
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const locationMenuContainer = document.querySelector(`.${styles.locationBtnCont}`);
      if (locationMenuContainer && !locationMenuContainer.contains(event.target)) {
        setIsLocationMenuOpen(false);
      }
    };

    if (isLocationMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLocationMenuOpen]);

  const serializedLocation = (location) =>
    location
      ? JSON.stringify({
          full_address: location.full_address,
          lat: location.lat,
          lon: location.lon,
          aroundRadius: sliderValue,
        })
      : null;

  const onSearchClick = () => {
    setfilters((prevFilters) => ({
      ...prevFilters,
      query: keywordValue,
      location: locationValue
        ? {
            full_address: locationValue?.full_address,
            lat: locationValue.lat,
            lon: locationValue.lon,
            aroundRadius: sliderValue,
          }
        : null,
      page: DEFAULT_PAGE,
      usHeadquarter: isUsHeadquarter,
      include_remote: includeRemote,
    }));
    updateSearchParams('query', keywordValue?.trim());
    updateSearchParams('id', '');
    updateSearchParams('include_remote', includeRemote);
    updateSearchParams('location', serializedLocation(locationValue));

    setValueSelected(true);
  };

  const handleLocationChange = useCallback(
    (location) => {
      setfilters((prevFilters) => ({
        ...prevFilters,
        location: location
          ? {
              full_address: location?.full_address,
              lat: location.lat,
              lon: location.lon,
              aroundRadius: sliderValue,
            }
          : null,
        page: DEFAULT_PAGE,
        usHeadquarter: isUsHeadquarter,
        include_remote: includeRemote,
      }));
      updateSearchParams('id', '');
      updateSearchParams('include_remote', includeRemote);
      updateSearchParams('location', serializedLocation(location));
      setLocationValue(location);
      setIsLocationMenuOpen(false);
    },
    [sliderValue, includeRemote]
  );

  const handleLocationMenuClick = () => {
    setIsLocationMenuOpen(!isLocationMenuOpen);
  };

  return (
    <div className={styles.searchFiltersCont}>
      <div className={styles.searchLocationContainer}>
        <div className={styles.searchCont}>
          <SearchByKeywords
            placeholder={t('search.placeholder')}
            setSearchTerm={(value) => setfilters({ ...filters, query: value, page: DEFAULT_PAGE })}
            keywordValue={keywordValue}
            setKeywordValue={setKeywordValue}
            setFetchJobsByName={setFetchJobsByName}
            valueSelected={valueSelected}
            setValueSelected={setValueSelected}
            filters={filters}
          />
        </div>
        <span className={styles.verticalLine} />
        <div className={classNames(styles.searchCont, { [styles.locationCont]: isMobile })}>
          <div className={styles.locationBtnCont}>
            <LocationSelect
              value={JSON.parse(searchParams.get('location'))}
              onChange={handleLocationChange}
              reverse
              placeholder={t('filters.locationPlaceholder')}
              disableFloatingLabel
              organizationLocations={organizationLocations}
            />
            {locationValue && (
              <div
                className={styles.locationMenuButton}
                onClick={handleLocationMenuClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleLocationMenuClick();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span>⋮</span>
              </div>
            )}
            {isLocationMenuOpen && (
              <LocationFilterDropdown
                sliderValue={sliderValue}
                setSliderValue={setSliderValue}
                isUsHeadquarter={isUsHeadquarter}
                onSearchClick={onSearchClick}
                setIncludeRemote={setIncludeRemote}
                includeRemote={includeRemote}
                setIsLocationMenuOpen={setIsLocationMenuOpen}
              />
            )}
          </div>
        </div>
        <ButtonWithIcon
          disabled={!locationValue && !keywordValue}
          text={t('search.searchButton')}
          containerClass={classNames({ [styles.hideSearchBtn]: isMobile })}
          imgClassName="btn-icon-36"
          textClassName="btn-icon-text-primary-40"
          variant="secondary-36"
          onClick={onSearchClick}
        />
      </div>
      <span className={styles.horizontalLine} />
      <div className={styles.filterOptionsCont}>
        <BrowseFiltersOption
          isDropdownVisible={isDropdownVisible}
          setIsDropdownVisible={setIsDropdownVisible}
          setfilters={setfilters}
          filters={filters}
        />
        <SearchByButtons filters={filters} setfilters={setfilters} />
      </div>
      {isDropdownVisible && (
        <div className={styles.filterDropdown} ref={inputRef}>
          <FilterDropdown
            isDropdownVisible={isDropdownVisible}
            setIsDropdownVisible={setIsDropdownVisible}
            setfilters={setfilters}
            inputRef={inputRef}
          />
        </div>
      )}
    </div>
  );
};

export default OpeningSearchAndFilter;
